import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormModeEnum } from '../../enums/form-mode.enum';
import {
    FormFieldActionGuidanceEnum, FormFieldExpressionListener, IFormFieldExpressionActionListener, IFormFieldAttributeSetter
} from '../../models/field-conditional-logic/form-field-expression-listener';
//import { FormFieldActionNameEnum } from '../../models/field-conditional-logic/enums';
import { FormFieldAction } from '../../models/field-conditional-logic/form-field-action.model';
import { FormFieldExpression } from '../../models/field-conditional-logic/form-field-expression.model';
import { FormFieldPropertyEnum } from '../../models/form-builder/form-field-property-enum.model';
import { FormField } from '../../models/form-builder/form-field.model';
import { FormInstanceElement } from '../../models/form-builder/form-instance-element.model';
import { Form } from '../../models/form-builder/form.model';
import { LayoutCell } from '../../models/form-builder/layout-cell.model';
import { LayoutRow } from '../../models/form-builder/layout-row.model';
import { LayoutCellInfo } from '../../models/form-builder/form-layout.model';
import { FormInstance } from '../../models/site-content/form-instance.model';
import { FormInstanceService } from '../../services/form-instance.service';
import { WorkflowTransition } from '../../models/workflow-transition.model';
import { FormFieldBaseComponent } from '../form-fields/form-field-base/form-field-base.component';
import { FormFieldWrapperComponent } from '../form-fields/form-field-wrapper/form-field-wrapper.component';
import { FieldDefinition } from '../../models/form-builder/field-definition.model';
import { FieldDefinitionService } from '../../services/field-definition.service';
import { IFieldDefinitionLogic } from '../../interfaces/ifield-definition-logic.interface';
import { IFormPreRenderFilter } from '../../interfaces/iform-pre-render-filter.interface';
import { CurrentSiteService } from '../../services/current-site.service';
import { RoleService } from '../../services/role.service';
import { FormSettings } from '../../models/form-builder/form-settings.model';
import { FormInstanceElementRequest } from '../../models/form-instance-element-request.model';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { AlertDialogComponent, AlertDialogModel } from '../../dialogs/alert/alert-dialog.component';
import { ScriptableBaseComponent } from '../../components/scriptable-base/scriptable-base.component';
import { FormFieldActionInstructions, FormFieldInstructionsByActionNameHash } from '../../models/field-conditional-logic/field-action-instructions.model';
import { FieldConditionalLogicService } from '../../services/field-conditional-logic.service';
import { Scriptable } from '../../models/component-scripting/scriptable-function.annotation';
import { IFormFieldCellEditorDialogDataSource } from '../../models/grid-cell-editor/igrid-cell-editor-dialog-data-source';
import { FormInstanceCellEditorDataSource } from '../../models/grid-cell-editor/form-instance-cell-edit-data-source.model';
import { FlexibleSelectionFieldService } from '../../services/flexible-selection-field.service';
import { Router } from '@angular/router';
import { TargetFieldConditionalLogicInstructions } from '../../models/site-metadata/target-field-logic-instructions';
import { CommunicationService } from '../../services/communication.service';
import { FieldIdToSelectedValues } from '../../models/flexible-selection-fields/flexible-selection-field-instructions-request.model';
import { ValueConcatenatorService } from '../../services/value-concatenator.service';

// Define internally used interfaces.
interface IFormInstanceElementsHash {
    [id: number]: FormInstanceElement;
}

interface IFormFieldWrapperComponentByIdHash {
    [clientId: number]: FormFieldWrapperComponent;
}

interface IFormFieldWrapperElementRefsByIdHash {
    [clientId: number]: ElementRef;
}

// Define properties used for show to/hide from logic.
class ShowToHideFromProperties {
    public hasShowToHideFromLogic: boolean = false;
    public siteRoleNames: string[] = [];
    public selectedRoleNames: string[] = [];
}

// Note:  the following, poorly named class, used only in this file, track form field component data for use in field conditional logic (not the same as so-called 'show to/hide from'' logic)
class FormRendererComponentData {
    // Properties.
    // Define properties that are not strictly needed but convenient when troubleshooting.
    public formFieldName: string;
    public formFieldDisplayName: string;

    // Define properties that we need.
    public wrapperComponent: FormFieldWrapperComponent = null;
    public formField: FormField = null;
    public elementRef: ElementRef = null;

    public constructor(wrapperComponent: FormFieldWrapperComponent, formField: FormField, elementRef: ElementRef) {
        this.formFieldName = formField.name;
        this.formFieldDisplayName = formField.displayName;

        this.wrapperComponent = wrapperComponent;
        this.formField = formField;
        this.elementRef = elementRef;
    }
}

class RendererWorkflowTransitionProperties {
    workflowTransitions: WorkflowTransition[] = null;

    workflowTransitionsBefore: WorkflowTransition[] = [];
    workflowTransitionsAfter: WorkflowTransition[] = [];
    currentWorkflowTransition: WorkflowTransition = null;
}

class RecordedSetFieldDataValue {
    public constructor(public fieldName: string, public fieldValue: string) { }
}

class FieldConditionalLogicStructures {
    // Properties.
    public formFieldComponentWrappersArray: FormFieldWrapperComponent[] = null;
    public formFieldComponentElementRefsArray: ElementRef[] = null;

    public mapOfFormFieldComponentElementRefsById: IFormFieldWrapperElementRefsByIdHash = null;
    public mapOfFormFieldInstructionsByAction: FormFieldInstructionsByActionNameHash = null;

    // Note:  property 'pendingTargetFieldInstructions' maintains a list of target field instructions
    //        that have not yet been applied, presumably the instructions arrived before the target
    //        field had completed initialization and emiting the 'onInit' output.
    public pendingTargetFieldInstructions: TargetFieldConditionalLogicInstructions[] = [];

    // Methods.
    public replaceFormFieldWrapperComponent(wrapperComponentIndex: number, wrapperComponent: FormFieldWrapperComponent): boolean {
        let success: boolean = false;

        if (this.formFieldComponentWrappersArray != null) {
            if ((wrapperComponentIndex < this.formFieldComponentWrappersArray.length) && (wrapperComponentIndex >= 0)) {
                this.formFieldComponentWrappersArray.splice(wrapperComponentIndex, 1, wrapperComponent);
                success = true;
            }
        }

        return success;
    }
    public replaceFormFieldWrapperElementRef(wrapperComponentIndex: number, elementRef: ElementRef): boolean {
        let success: boolean = false;

        if (this.formFieldComponentElementRefsArray != null) {
            if ((wrapperComponentIndex < this.formFieldComponentElementRefsArray.length) && (wrapperComponentIndex >= 0)) {
                this.formFieldComponentElementRefsArray.splice(wrapperComponentIndex, 1, elementRef);
                success = true;
            }
        }

        return success;
    }
}

class FormFieldActionAppliedFlags {
    public constructor(public fieldActionApplied: boolean = false, public fieldShown: boolean = false, public fieldHidden: boolean = false) {
    }
}

// 11-25-2024 note:  added the following class to be used in the future.
class FormRendererFormFieldOnInitInfo {
    public component: FormFieldBaseComponent;
    public formField: FormField;
}

// Define/implement my component.
@Component({
    selector: 'app-form-renderer',
    templateUrl: './form-renderer.component.html',
    styleUrls: ['./form-renderer.component.scss'],
    standalone: false
})
export class FormRendererComponent extends ScriptableBaseComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, IFormFieldExpressionActionListener {

    // Inputs.
    @Input()
    form: Form;

    @Input()
    formInstance: FormInstance = null;

    @Input()
    mode: string;

    //VNEXT-950: KLW - Logic to show and hide the form and transition buttons
    @Input()
    showSaveDiscard: boolean = true;
    @Input()
    showTransitions: boolean = true;

    @Input()
    set buttonsEnabled(value: boolean) {
        this.formInstance.formButtonsDisabled = !value;
    }

    @Input() isClosable: boolean;

    // Outputs.
    @Output() onSave = new EventEmitter();
    @Output() onTransition = new EventEmitter();
    @Output() notifySubscribers = new EventEmitter();
    @Output() onReset = new EventEmitter();
    @Output() onRequestService = new EventEmitter();
    @Output() onClose = new EventEmitter();
    @Output() newObjectCreated = new EventEmitter();

    // Additional properties.
    private bSavePending: boolean = false;
    private hshDataToRememberDuringSave: any = null;

    // Form instance elements hash.
    private hshFormInstanceElements: IFormInstanceElementsHash = {};
    // pharvey - added 10/28/2021 to prevent form save when any field is invalid.
    //private formIsInvalid: boolean; // Paul:  is this property being used?
    // pharvey - added 10/28/2021 to prevent form save when any field is invalid.
    // It gets set to true on form save when the form is invalid.
    // It is provided as input to FormFieldWrapper to instruct it to display all validation errors of all form fields
    private revealValidationErrors: boolean = false;

    private expressionListener: FormFieldExpressionListener = null;

    @ViewChildren(FormFieldWrapperComponent, { read: FormFieldWrapperComponent }) formFieldComponentWrappers: QueryList<FormFieldWrapperComponent>;
    @ViewChildren(FormFieldWrapperComponent, { read: ElementRef }) formFieldComponentElementRefs: QueryList<ElementRef>;
    //private hshFormFieldComponentElementRefsById: IFormFieldWrapperElementRefsByIdHash = null;
    //private hshFormFieldInstructionsByAction: FormFieldInstructionsByActionNameHash = null;
    private conditionalLogicStructures: FieldConditionalLogicStructures = new FieldConditionalLogicStructures();

    private showToHideFromProperties: ShowToHideFromProperties = new ShowToHideFromProperties();

    private formSettings: FormSettings = new FormSettings();

    private workflowTransitionProperties: RendererWorkflowTransitionProperties = new RendererWorkflowTransitionProperties();
    private recordedFieldValues: RecordedSetFieldDataValue[] = null;

    // Flex fields have the need to know about the values, in the client, of other fields
    // This is because when looking up the options for a flex field, the values of any chain of fields it depends on have to be looked up
    // This dictionary used to be the flexFieldService but it makes sense to put it here so it can be universally accessed
    // (FlexibleSeelctionService has a reference to the FormRenderer and so can access this, but other fields could do this if they needed to)
    private selectedValuesByFieldId: FieldIdToSelectedValues = {};

    // 11-25-2024 note:  will test and re-enable the following data structure in the future.
    //private arrFormFieldOnInitData: FormRendererFormFieldOnInitInfo[] = [];

    // Constructor.
    public constructor(public dialog: MatDialog,
        private renderer: Renderer2,
        private router: Router,
        private formInstanceService: FormInstanceService,
        private currentSiteService: CurrentSiteService,
        private roleService: RoleService,
        private fieldDefinitionService: FieldDefinitionService,
        private fieldConditionalLogicService: FieldConditionalLogicService,
        private flexibleSelectionFieldService: FlexibleSelectionFieldService,
        private fieldValueConcatenatorService: ValueConcatenatorService,
        private communicationService: CommunicationService,
        elForBaseClass: ElementRef) {
        super(elForBaseClass);

        //this.formFieldTypeAndNameService.initializeFieldTypesAndNames(this.renderer, this.dateAdapter, this.attachmentService, this.dialog);
    }

    // Workflow-related methods.
    public get WorkflowStateTransitions(): WorkflowTransition[] {

        if (this.formInstance != null) {
            if (this.workflowTransitionProperties.workflowTransitions == null || this.workflowTransitionProperties.workflowTransitions != this.formInstance.workflowStateTransitions) {
                this.workflowTransitionProperties.workflowTransitions = this.formInstance.workflowStateTransitions;
                this.workflowTransitionProperties.workflowTransitions.sort((n1, n2) => {
                    if (n1.endStateSortOrder > n2.endStateSortOrder) {
                        return 1;
                    }

                    if (n1.endStateSortOrder < n2.endStateSortOrder) {
                        return -1;
                    }

                    return 0;
                });
            }
        }

        return this.workflowTransitionProperties.workflowTransitions;
    }

    public get SelectedValuesByFieldId(): FieldIdToSelectedValues {
        return this.selectedValuesByFieldId;
    }

    public hasDependentFields(formField: FormField) {
        //TODO: FIX THIS!
        return true;
    }

    public workflowTransitionClicked(workflowTransition: WorkflowTransition, eventData: any): void {
        this.formInstance.selectedWorkflowTransition = workflowTransition.name;
        //TEAMS-178: KLW - Set the end state id of the selected workflow transition
        this.formInstance.selectedWorkflowTransitionEndStateId = workflowTransition.endStateId;
        //VNEXT-310: KLW - Be sure to disable the buttons while a transition is being performed
        this.formInstance.formButtonsDisabled = true;
        //this.onTransition.emit(workflowTransition.name);
        this.onTransition.emit(workflowTransition);
    }

    public sendNotificationToAllSubs(eventData: any) {
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            disableClose: true,
            maxWidth: "600px",
            data: new ConfirmationDialogModel("Send Notifcations", "A notification will be sent from you to all subscribers of this Form. <br/>Do you wish to proceed?")
        });

        return dialogRef.afterClosed().toPromise().then(yes => {
            if (yes) {
                this.notifySubscribers.emit();
            }
        });
    }

    public get CurrentWorkflowTransition(): WorkflowTransition {
        if (this.formInstance != null) {
            if ((this.workflowTransitionProperties.currentWorkflowTransition == null && this.formInstance.workflowStateTransitions.length > 0) || this.workflowTransitionProperties.workflowTransitions != this.formInstance.workflowStateTransitions) {
                this.workflowTransitionProperties.workflowTransitions = this.formInstance.workflowStateTransitions;
                this.workflowTransitionProperties.workflowTransitionsAfter = [];
                this.workflowTransitionProperties.workflowTransitionsBefore = [];

                let toAdd: WorkflowTransition = new WorkflowTransition();
                toAdd.name = this.formInstance.currentWorkflowState;
                toAdd.endStateSortOrder = this.formInstance.currentWorkflowStateOrder;
                toAdd.isCurrentState = true;

                this.workflowTransitionProperties.currentWorkflowTransition = toAdd;

                this.formInstance.workflowStateTransitions.forEach((x) => {
                    if (x.endStateSortOrder > this.workflowTransitionProperties.currentWorkflowTransition.endStateSortOrder) {
                        this.workflowTransitionProperties.workflowTransitionsAfter.push(x);
                    }

                    if (x.endStateSortOrder < this.workflowTransitionProperties.currentWorkflowTransition.endStateSortOrder) {
                        this.workflowTransitionProperties.workflowTransitionsBefore.push(x);
                    }
                });
            }
        }
        return this.workflowTransitionProperties.currentWorkflowTransition;
    }

    // Life cycle methods.
    public ngOnInit(): void {

        // If we have form instance elements, assign them to the appropriate cells.
        //
        // 03-28-2023 note:  the following call repeats what is already performed prior in ngOnChanges(), so we should experiment with removing the following line.
        this.assignCellFormInstanceElement();
        this.setFieldsReadOnlyIfUserCannotEdit();

        // Handle any conditional logic rule setup.
        //this.performConditionalLogicRuleSetupIfApplicable();

        // Handle any show to/hide from logic.
        let dataCollectionId: number = this.currentSiteService.Site.id;
        //this.roleService.getRolesUsedInSite(dataCollectionId).then(roleNames => {
        this.determineIfFormHasShowToHideFromLogic();
        if (this.showToHideFromProperties.hasShowToHideFromLogic) {
            this.roleService.getSiteRoles(dataCollectionId).then(roles => {
                this.showToHideFromProperties.siteRoleNames = roles.map(r => r.name);
                this.showToHideFromProperties.selectedRoleNames = [];
                this.performShowToHideFromLogicIfApplicable();
            });
        }

        if (this.form.jsonConfig) {
            let fs: FormSettings = JSON.parse(this.form.jsonConfig);

            if (fs) {
                this.formSettings.hideSaveButton = fs.hideSaveButton;
                this.formSettings.saveButtonCaption = fs.saveButtonCaption;
                this.formSettings.confirmSavePopup = fs.confirmSavePopup;
                this.formSettings.enableBulkWorkflow = fs.enableBulkWorkflow;
            }
        }

        // flexibleSelectionFieldService will use a reference to this FormRenderer in order to access any Fields it needs to update
        this.flexibleSelectionFieldService.FormRenderer = this;

        // Request field conditional logic instructions.
        //this.requestFieldConditionalLogicInstructions();
        // Request flex field instructions.
        //this.initiallyHideFieldsThatAreTargetsOfConditionalLogic();
        this.requestFlexFieldInstructions();
        this.populateSelectedValuesByFieldIdForFlexFields();
        this.fieldValueConcatenatorService.updateAllFieldValues(this.formInstance, this.form);
    }

    public ngAfterViewInit(): void {
        // Note:  @ViewChild() and @ViewChildren() value can be seen when this method is called but generally not before.
        this.loadConditionalLogicComponentDataStructures();
        this.initiallyHideFieldsThatAreTargetsOfConditionalLogic();

        // Load the form field action instructions hash.
        //
        // 01-31-2024 note:  the following hash should be moved into service class FieldConditionalLogicService.
        {
            //this.hshFormFieldInstructionsByAction = {};
            this.conditionalLogicStructures.mapOfFormFieldInstructionsByAction = {};

            let arrFormFieldActionInstructions: FormFieldActionInstructions[] = this.fieldConditionalLogicService.getFormFieldActionInstructions();
            for (let iAction: number = 0; iAction < arrFormFieldActionInstructions.length; iAction++) {
                let actionInstructions: FormFieldActionInstructions = arrFormFieldActionInstructions[iAction];

                //this.hshFormFieldInstructionsByAction[actionInstructions.actionName] = actionInstructions;
                this.conditionalLogicStructures.mapOfFormFieldInstructionsByAction[actionInstructions.actionName] = actionInstructions;
            }
        }

        // Request field conditional logic instructions.
        //this.requestFieldConditionalLogicInstructions();
        // 11-25-2024 note:  added the following delay to avoid the dreaded "a value changed while the template was being drawn" error.
        setTimeout(() => {
            this.requestFieldConditionalLogicInstructions();
        }, 1500);

        // Now that everything is set up, call my base class's ngOnInit() method.
        super.ngOnInit();
    }

    public ngOnDestroy() {
    }

    // Accessor methods.
    public get formButtonsDisabled() {
        return this.formInstance.formButtonsDisabled;
    }

    public get IsClosable(): boolean {
        return this.isClosable;
    }

    //VNEXT-950: KLW - Logic to show and hide the form and transition buttons
    public get ShowSaveDiscard() {
        return this.showSaveDiscard;
    }

    public get ShowTransitions() {
        return this.showTransitions;
    }

    public getFormField(id: number, name: string): FormField {
        let ff: FormField;

        let list = this.form.formFields.filter(x => (id && id > 0) ? x.id == id : x.name == name);

        if (list)
            ff = list[0];
        else
            alert('error, form field not found');

        return ff;
    }

    public get RevealValidationErrors(): boolean {
        return this.revealValidationErrors;
    }

    public get HideSaveButton(): boolean {
        if (this.ReadOnly) return true;
        if (this.formSettings) {
            return this.formSettings.hideSaveButton;
        }
        else { return false; }
    }

    public get ReadOnly(): boolean {
        // Note:  we will not have a formInstance value in 'Preview'
        // mode, but do not force read only mode because of that.
        return (this.formInstance != null ? !this.formInstance.canEdit : false);
    }

    public get IsOriginalFormInstance(): boolean {
        return ((this.formInstance != null) && (this.formInstance.version == 0));
    }

    public get HasShowToHideFromLogic(): boolean {
        return this.showToHideFromProperties.hasShowToHideFromLogic;
    }
    public get SiteRoleNames(): string[] {
        return this.showToHideFromProperties.siteRoleNames;
    }
    public get SelectedRoleNames(): string[] {
        return this.showToHideFromProperties.selectedRoleNames;
    }
    public set SelectedRoleNames(value: string[]) {
        this.showToHideFromProperties.selectedRoleNames = value;
    }

    public get FormSaved(): boolean {
        return this.form.id > 0;
    }

    public ShowRowContent(layoutRow: LayoutRow): boolean {
        return !layoutRow.transientIsDeleted;
    }
    public ShowCellContent(cell: LayoutCell): boolean {
        let show: boolean = (cell.formFieldId > 0 || cell.hasContent);
        if (show && (cell.transientIsHidden == true))
            show = false;
        if (show && (this.mode == FormModeEnum.PREVIEW)) {
            let formField: FormField = this.getFormField(cell.formFieldId, cell.name);
            if ((formField != null) && formField.transientIsDeleted)
                show = false;
        }
        return show;
    }

    public getTransientSetHeightToZero(row: LayoutRow, form: Form, rowIndex: number): boolean {
        let showAsZeroHeight: boolean = false;

        if (row.cells != null) {
            let numNonEmptyCells: number = 0;
            let numHiddenCells: number = 0;

            for (let cellIndex: number = 0; cellIndex < row.cells.length; cellIndex++) {
                let cell: LayoutCell = row.cells[cellIndex];

                if (cell.hasContent)
                    numNonEmptyCells++;
                if (cell.transientIsHidden)
                    numHiddenCells++;
            }

            if ((numNonEmptyCells > 0) && (numNonEmptyCells == numHiddenCells))
                showAsZeroHeight = true;
        }

        return showAsZeroHeight;
    }

    // Handle control events.
    public handleOnInit(eventData: any, cell: LayoutCell): void {
        // If this is a component that requires a call to method saveConfiguration()
        // (defined on class FormFieldBaseComponent), save a reference to it.
        if (eventData.properties) {
            let arrProperties: string[] = <string[]>eventData.properties;

            let arrServiceRequiredProp: string[] =
                arrProperties.filter(p => p == FormFieldPropertyEnum.FORM_INSTANCE_SERVICE_REQUIRED);

            if (arrServiceRequiredProp && (arrServiceRequiredProp.length == 1)) {
                let hshOutputData = {};

                this.onRequestService.emit(hshOutputData);

                if (hshOutputData['service']) {
                    eventData['service'] = hshOutputData['service'];
                }
            }
        }

        // 11-25-2024 note:  will re-test and enable the following code block in the future.
        /*
        // Save form field/component data.
        let formFieldOnInitData: FormRendererFormFieldOnInitInfo = new FormRendererFormFieldOnInitInfo();
        formFieldOnInitData.component = eventData['component'];
        formFieldOnInitData.formField = eventData['formField'];
        this.arrFormFieldOnInitData.push(formFieldOnInitData);
        */

        // If we have built our conditional logic structures, replace the information for the emitting
        // component as it could be more up to date than the original information found for that component.
        //
        // Note:  the call to the following method is presently not being used, but it is one approach to handle
        //        the data structure updates associated with the two @ViewChildren properties, formFieldComponentWrappers
        //        and formFieldComponentElementRefs, defined in this class, FormRendererComponent.
        //
        // Note:  the call to the following method will be enabled if and when testing indicates that the
        //        data structures associated with form field wrapper components and/or the corresponding form
        //        field wrapper element references fall into inconsistent states that either undermine the form
        //        field conditional logic, cause some sort of error, or both.
        //
        // Note:  the most reliable way to manage form field components and corresponding element refs that need
        //        to be removed from the HTML DOM would seem to be adding a property to FormFieldWrapperCompponent
        //        telling it to disable (*ngIf) the HTML associated with a wrapper form field component when such
        //        a component needs to be hidden by form field conditional logic.
        //this.updateFormFieldConditionalLogicInfo(eventData);

        // If any field conditional logic target field instructions are pending, try to apply them now.
        this.reapplyAnyPendingFieldConditionalLogicInstructions();
    }

    public requestFormInstanceIds(eventData: any): void {
        // The caller is requesting Id information -- provide it.
        if (this.formInstance) {
            eventData['dataCollectionId'] = this.formInstance.dataCollectionId;
            eventData['folderId'] = this.formInstance.folderId;
            eventData['formInstanceId'] = this.formInstance.id;
        }
        else {
            console.log('formInstance not defined during requestFormInstanceIds');
        }
        return;
    }

    // Handle ngModelChangeEvents.
    public handleNgOnFormInstanceElementChange(layoutRow: LayoutRow, layoutCell: LayoutCell): void {
        // If we have an expression listener,
        // pass this updated field value to it.
        if (this.expressionListener) {
            let formField: FormField = this.getFormField(layoutCell.formFieldId, layoutCell.name);

            if (!formField) {
                let errorMsg: string = `FormRenderComponent.handleNgOnChange():  cannot find form field for cell ` + `'${layoutCell.name}', form field ${layoutCell.formFieldId}).`;
                this.throwError(errorMsg);
            }

            let formInstanceElement: FormInstanceElement = layoutCell.formInstanceElement;

            if (formInstanceElement == null) {
                let errorMsg: string = `FormRenderComponent.handleNgOnChange():  cannot find form instance element for cell ` + `'${layoutCell.name}', form field ${layoutCell.formFieldId}).`;
                this.throwError(errorMsg);
            }

            // 02-03-2024 note:  temporarily, let run all of the form field conditional logic, for all fields, whenever any value changes.
            //this.expressionListener.formFieldValueChanged(formField, formInstanceElement);
            // 02-23-2024 NOTE:  NEED TO DECIDE WHEN TO DISABLE THIS ENTIRE METHOD.
            // 02-23-2024 note:  disabling the following line in order to start testing the server-side field conditional logic that will replace this client-side logic.
            //this.performConditionalLogicRuleSetupIfApplicable();
        }

        return;
    }

    public handleLoadFormInstanceElement(loadRequest): void {
        let request: FormInstanceElementRequest = loadRequest.request;
        let component: FormFieldBaseComponent = loadRequest.component;

        this.formInstanceService.getFormInstanceElement(request).then(formInstanceResponse => {
            if (formInstanceResponse && (formInstanceResponse.formInstanceElements != null) && (formInstanceResponse.formInstanceElements.length == 1)) {
                let formInstanceElement: FormInstanceElement = formInstanceResponse.formInstanceElements[0];
                component.formInstanceElementLoaded(formInstanceElement);
            }
        });

    }

    // Handle ngOnChanges().
    public ngOnChanges(changes: SimpleChanges) {
        // My parent component saving my form instance
        // causes this method to execute, allowing this
        // component and the form field components it
        // instantiates to get updated form instance
        // elements.
        //
        // This matters most when form instance
        // elements are first saved, when form instance
        // element IDs are created.  Henceforth the
        // previously saved form instance elements can
        // be re-saved rather than creating new ones.

        if (changes.formInstance) {
            // Assign form instance elements.
            this.assignCellFormInstanceElement();
            // Reset my save pending flag.
            this.bSavePending = false;
        }

        return;
    }

    public returnToHome() {
        this.router.navigateByUrl(`/site/${this.form.dataCollectionId}`);
    }

    // Implement the IFormFieldExpressionActionListener interface method.
    public expressionTriggeredAction(formField: FormField, expressionForDebuggingOnly: FormFieldExpression, action: FormFieldAction, actionGuidance: FormFieldActionGuidanceEnum): void {
        // Find the field component associated with this form field.
        if ((this.formFieldComponentWrappers != null) && (this.formFieldComponentElementRefs != null)) {
            let componentData: FormRendererComponentData = this.findFormFieldComponentDataFor(action.targetFieldName);

            //let actionInstructions: FormFieldActionInstructions = this.hshFormFieldInstructionsByAction[action.actionName];
            let actionInstructions: FormFieldActionInstructions = this.conditionalLogicStructures.mapOfFormFieldInstructionsByAction[action.actionName];
            if (actionGuidance == FormFieldActionGuidanceEnum.RemoveAction) {
                //actionInstructions = this.hshFormFieldInstructionsByAction[actionInstructions.oppositeActionName];
                actionInstructions = this.conditionalLogicStructures.mapOfFormFieldInstructionsByAction[actionInstructions.oppositeActionName];
            }

            if (componentData != null) {
                // Apply the action's instructions to the designated form field.
                //
                // Start with removing any CSS styles/classes.
                let actionAppliedFlags: FormFieldActionAppliedFlags = new FormFieldActionAppliedFlags();
                FormRendererComponent.applyStylesUsing(actionInstructions.applyActionStylesToRemove, componentData.elementRef, this.renderer, 'removeClass', actionAppliedFlags);

                // Add any specified CSS classes for this action.
                FormRendererComponent.applyStylesUsing(actionInstructions.applyActionStyles, componentData.elementRef, this.renderer, 'addClass', actionAppliedFlags);

                // If we have just shown or hidden a field, we might need to show or hide the field's entire row.
                if (actionAppliedFlags.fieldActionApplied) {
                    let cellInfo: LayoutCellInfo = this.form.formLayout.findCellInfoContaining(componentData.formField.name);
                    if (cellInfo != null) {
                        cellInfo.cell.transientIsHidden = actionAppliedFlags.fieldHidden;

                        let layoutRow: LayoutRow = this.form.formLayout.rows[cellInfo.rowIndex];
                        if (layoutRow.cells != null) { // This condition of course should never be false.
                            let numHiddenCells: number = 0;
                            let numShownCells: number = 0;
                            for (let cellIndex: number = 0; cellIndex < layoutRow.cells.length; cellIndex++) {
                                let cell: LayoutCell = layoutRow.cells[cellIndex];
                                if (cell.transientIsHidden)
                                    numHiddenCells++;
                                else
                                    numShownCells++;
                            }

                            // Find the layout row element.
                            let rowDivElement: ElementRef = FormRendererComponent.findParentDivContainingCssClassFor(componentData.elementRef, 'form-row');
                            let rowDivClassList: any = rowDivElement['classList'];
                            if ((rowDivElement != null) && (rowDivClassList != null)) {
                                // If we need to either show or hide the row div by setting its height, do it.
                                if (numHiddenCells == layoutRow.cells.length) {
                                    /*
                                    let containsStyle: boolean = rowDivClassList['contains']('apply-hide-row-action'); 

                                    if (!containsStyle) {
                                        this.renderer.removeClass(rowDivElement, 'apply-show-row-action')
                                        this.renderer.addClass(rowDivElement, 'apply-hide-row-action');
                                    }
                                    */

                                    //setTimeout(() => { //delay avoids "Expression has changed after it was checked. Previous value: 'form-row'. Current value: 'form-row hidden-form-row'""
                                    //    layoutRow.transientSetHeightToZero = true;
                                    //}, 1);
                                } else if (numShownCells > 0) {
                                    /*
                                    let containsStyle: boolean = rowDivClassList['contains']('apply-show-row-action');

                                    if (!containsStyle) {
                                        this.renderer.removeClass(rowDivElement, 'apply-hide-row-action')
                                        this.renderer.addClass(rowDivElement, 'apply-show-row-action');
                                    }
                                    */
                                    //layoutRow.transientSetHeightToZero = false;
                                }
                            }
                        }
                    }
                }

                // Apply form field properties, if any.
                //
                // Note:  we should clean up the following switch statement, replacing it with
                //        a call to an interface or something that is not conditional logic.
                if (actionInstructions.appliesFormFieldAttribute) {
                    let attributeSetter: IFormFieldAttributeSetter = this.expressionListener.getFormFieldAttributeSetterForAttribute(actionInstructions.formFieldAttributeName);
                    if (attributeSetter != null)
                        attributeSetter.setFormFieldAttribute(componentData.formField, actionInstructions.formFieldAttributeName, actionInstructions.formFieldAttributeValue);
                } // if
            } // if
        } // if

        return;
    }

    private static findParentDivContainingCssClassFor(elementRef: ElementRef, cssClassName: string): ElementRef {
        let divElementRef: ElementRef = null;

        let parentElement: ElementRef = elementRef.nativeElement.parentElement;
        let numParentsTraversed: number = 0;
        while (parentElement != null) {
            numParentsTraversed++;

            if (parentElement['localName'] == 'div') {
                let classList: any = parentElement['classList'];
                let containsCssClass: boolean = classList['contains'](cssClassName);
                if (containsCssClass) {
                    divElementRef = parentElement;
                    break;
                }
            }

            parentElement = parentElement['parentElement'];
        }

        return divElementRef;
    }

    private findFormFieldComponentDataFor(formFieldName: string): FormRendererComponentData {
        let targetWrapperComponent: FormFieldWrapperComponent = null;
        let targetFormField: FormField = null;
        let targetElementRef: ElementRef = null;

        //let formFieldComponents: FormFieldWrapperComponent[] = this.formFieldComponentWrappers.toArray();
        //let formFieldElementRefs = this.formFieldComponentElementRefs.toArray();

        //if ((formFieldComponents != null) && (formFieldElementRefs != null) && (formFieldComponents.length == formFieldElementRefs.length)) {
        if ((this.conditionalLogicStructures.formFieldComponentWrappersArray != null) && (this.conditionalLogicStructures.formFieldComponentElementRefsArray != null) &&
            (this.conditionalLogicStructures.formFieldComponentWrappersArray.length == this.conditionalLogicStructures.formFieldComponentElementRefsArray.length)) {
            //for (let index: number = 0; index < formFieldComponents.length; index++) {
            for (let index: number = 0; index < this.conditionalLogicStructures.formFieldComponentWrappersArray.length; index++) {
                // 04-02-2024 note:  added the following if statement.
                //
                // Note:  need to discuss the following if statement with Paul or Steve.
                //if ((formFieldComponents[index].WrappedFormComponent != null) && (formFieldComponents[index].WrappedFormComponent.FormField != null)) {
                if ((this.conditionalLogicStructures.formFieldComponentWrappersArray[index].WrappedFormComponent != null) && (this.conditionalLogicStructures.formFieldComponentWrappersArray[index].WrappedFormComponent.FormField != null)) {
                    //if (formFieldComponents[index].WrappedFormComponent.FormField.name == formFieldName) {
                    if (this.conditionalLogicStructures.formFieldComponentWrappersArray[index].WrappedFormComponent.FormField.name == formFieldName) {
                        //targetWrapperComponent = formFieldComponents[index];
                        targetWrapperComponent = this.conditionalLogicStructures.formFieldComponentWrappersArray[index];
                        targetFormField = targetWrapperComponent.WrappedFormComponent.FormField;
                        //targetElementRef = formFieldElementRefs[index];
                        targetElementRef = this.conditionalLogicStructures.formFieldComponentElementRefsArray[index];

                        break;
                    }
                }
            }
        }

        return (targetWrapperComponent != null ? new FormRendererComponentData(targetWrapperComponent, targetFormField, targetElementRef) : null);
    }

    // Note:  please see the notes in method handleOnInit(), above, regarding why the following method is presently not being used.
    private updateFormFieldConditionalLogicInfo(eventData: any): void {
        // If we have built our conditional logic structures, replace the information for the emitting
        // component as it could be more up to date than the original information found for that component.
        if ((eventData['component'] != null) && (this.conditionalLogicStructures.formFieldComponentWrappersArray != null)) {
            let formFieldWrapperComponent: FormFieldWrapperComponent = <FormFieldWrapperComponent>eventData['component'];
            let formField: FormField = formFieldWrapperComponent.FormField;
            let existingComponentData: FormRendererComponentData = this.findFormFieldComponentDataFor(formField.name);

            if (existingComponentData != null) {
                existingComponentData.wrapperComponent = formFieldWrapperComponent;
                existingComponentData.formField = formField;

                this.replaceFormFieldComponentDataFor(formField.name, existingComponentData);
            }
        }
    }

    private replaceFormFieldComponentDataFor(formFieldName: string, componentData: FormRendererComponentData): void {
        if ((this.conditionalLogicStructures.formFieldComponentElementRefsArray != null) && (this.conditionalLogicStructures.formFieldComponentElementRefsArray != null) &&
            (this.conditionalLogicStructures.formFieldComponentWrappersArray.length == this.conditionalLogicStructures.formFieldComponentElementRefsArray.length)) {
            for (let index: number = 0; index < this.conditionalLogicStructures.formFieldComponentWrappersArray.length; index++) {
                if ((this.conditionalLogicStructures.formFieldComponentWrappersArray[index].WrappedFormComponent != null) && (this.conditionalLogicStructures.formFieldComponentWrappersArray[index].WrappedFormComponent.FormField != null)) {
                    if (this.conditionalLogicStructures.formFieldComponentWrappersArray[index].WrappedFormComponent.FormField.name == formFieldName) {
                        // Update the form field wrapper component data.
                        this.conditionalLogicStructures.replaceFormFieldWrapperComponent(index, componentData.wrapperComponent);
                        // Update the form field wrapper element ref.
                        // Note:  the following line will be enabled once it has been more thoroughly tested.
                        //this.conditionalLogicStructures.replaceFormFieldWrapperElementRef(index, componentData.elementRef);

                        break;
                    }
                }
            }
        }
    }

    private static applyStylesUsing(styles: string[], elementRef: ElementRef, renderer: Renderer2, rendererMethodName: string, actionAppliedFlags: FormFieldActionAppliedFlags): void {
        if ((styles != null) && (styles.length > 0)) {
            for (let iStyle: number = 0; iStyle < styles.length; iStyle++) {
                let cssClassName: string = styles[iStyle];

                // Note:  it makes sense to change the styling/CSS classes of the
                //        form field's parent element as that will be a <div/>
                //        created within the template of this class's .html.
                let parentElement = elementRef.nativeElement.parentElement;
                let parentElementContainsClass: boolean = elementRef.nativeElement.parentElement.classList.contains(cssClassName)
                //renderer[rendererMethodName](parentElement, cssClassName);

                if (rendererMethodName == 'addClass') {
                    // Apply this action if the parent element does not yet contain the CSS class.
                    if (true || !parentElementContainsClass) {
                        renderer.addClass(parentElement, cssClassName);

                        // Set any applicable action flags.
                        if (cssClassName == 'apply-show-action') {
                            actionAppliedFlags.fieldActionApplied = true;
                            actionAppliedFlags.fieldShown = true;
                        } else if (cssClassName == 'apply-hide-action') {
                            actionAppliedFlags.fieldActionApplied = true;
                            actionAppliedFlags.fieldHidden = true;
                        }
                    }
                } else if (rendererMethodName == 'removeClass') {
                    // Apply this action if the element contains the CSS class.
                    if (true || parentElementContainsClass) {
                        renderer.removeClass(parentElement, cssClassName);
                    }
                }
            }
        }
    }

    //TEAMS-561: KLW - Logic for validation and enabling the save button
    public IsFormValid(): boolean {
        let isValid: boolean = true;

        if (this.formFieldComponentWrappers) {
            if (this.formFieldComponentWrappers.length > 0) {
                for (let wr of this.formFieldComponentWrappers) {
                    if (wr) {
                        if (wr.WrappedFieldHasError) {
                            isValid = false;
                        }
                    }
                }
            }
        }
        return isValid;
    }

    public CheckFormValidations(): Promise<void> {

        return new Promise<void>((resolve, reject) => {

            if (this.formFieldComponentWrappers) {
                if (this.formFieldComponentWrappers.length > 0) {
                    for (let wr of this.formFieldComponentWrappers) {
                        if (wr) {
                            wr.WrappedFieldCheckValidation();
                        }
                    }
                }
            }

            resolve();
        });

    }

    public setFormValid(gridState: string): void {
        // Changes here for VNEXT-1119
        // Grid invalidity was not disabling the Save button
        if (!this.IsFormValid()) {
            this.saveDisabled = true;
        }
        else if (gridState === FormFieldPropertyEnum.VALID) {
            this.saveDisabled = false;
        }
        else if (gridState == FormFieldPropertyEnum.INVALID) {
            this.saveDisabled = true;
        }
        else if (this.IsFormValid()) {
            this.saveDisabled = false;
        }
    }

    private saveDisabled: boolean = false;

    public get SaveDisabled(): boolean {
        return this.saveDisabled && !this.formInstance.currentWorkflowStateIsStartState;
    }

    public buildFormInstance(allowDraftSave: boolean, saveIsFor: string = null): Promise<FormInstance> {
        // Check for and handle validation errors
        for (let wr of this.formFieldComponentWrappers) {
            if (wr.WrappedFieldHasError) {
                this.revealValidationErrors = true;
                if (allowDraftSave) {
                    return this.setupForSaveAsDraftUponConfirmation(saveIsFor);
                } else {
                    return;
                }
            }
        }

        // we only get here if there have been no validation errors
        return new Promise<FormInstance>((resolve, reject) => {
            let formInstance = this.onBuildFormInstance(false, true);
            resolve(formInstance);
        });
    }

    // pharvey - 03/20/2024 - for VNEXT-1184 (save drafts)
    // Displays the "Save Draft" confirmation for when validation errors have occurred.
    // If the user chooses "Save Draft" then onBuildFormInstance() is called which sets up
    // the formInstance's formIstanceElements ready for saving
    // If the user chooses "Cancel" then the nothing is setup and a null is returned to
    // indicate to callers that nothing should be saved
    private setupForSaveAsDraftUponConfirmation(saveIsFor: string): Promise<FormInstance> {
        let message = saveIsFor == "transition" ?
            "The form has validation errors. Either save as a draft in the current workflow state, or cancel and fix the errors." :
            "The form has validation errors. Either save as a draft or cancel and fix the errors.";
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            disableClose: true,
            maxWidth: "600px",
            data: new ConfirmationDialogModel("Validation Errors", message, { confirmButtonText: "Save Draft", dismissButtonText: "Cancel" })
        });

        return dialogRef.afterClosed().toPromise().then(yes => {
            if (yes) {
                this.formInstance.hasFailedClientValidation = true;
                this.formInstance.skipValidationOnUpdateContent = true;
                return this.onBuildFormInstance(true, false);
            } else {
                this.formInstance.formButtonsDisabled = false;
                return null;
            }
        });
    }

    // 01-21-2021 note:  allow form field components to provide data that will
    //                   be saved during a save operation and passed back
    //                   to the form field component when the save completes.
    private onBuildFormInstance(skipValidation: boolean, validationFailed: boolean) {
        this.formInstance.transientFormInstanceIsSaving = true;

        for (let component of this.formFieldComponentWrappers) {
            let hshComponentDataToSave: any = {};

            component.saveData(hshComponentDataToSave);
            //component.saveData(this.formInstance);
            if (Object.keys(hshComponentDataToSave).length > 0) {
                if (this.hshDataToRememberDuringSave == null) {
                    this.hshDataToRememberDuringSave = {};
                }

                let componentFormField: FormField = component.FormField;
                this.hshDataToRememberDuringSave[componentFormField.id] = hshComponentDataToSave;
            }
        }

        // Copy the unique form instance elements
        //  into the form instance's array of elements.
        let arrFormInstanceElements: FormInstanceElement[] = [];

        let astrKeys: string[] = Object.keys(this.hshFormInstanceElements);
        for (let iKey: number = 0; iKey < astrKeys.length; iKey++) {
            let strKey = astrKeys[iKey];
            let formInstanceElement: FormInstanceElement = this.hshFormInstanceElements[strKey];

            arrFormInstanceElements.push(formInstanceElement);
        }

        this.formInstance.formInstanceElements = arrFormInstanceElements;
        return this.formInstance;
    }

    // Handle control events.
    // save the form data.
    public saveClicked(): void {
        // wait for any grid saves to complete before saving the form as a whole
        // (Added for VNEXT-1294 to ensure grid data gets saved when saving a Form. The promise based approach eliminates possible race conditions)
        this.communicationService.saveAnyUnsavedGridData().then(res => {
            this.CheckFormValidations().then(() => {
                let allowDraftSave: boolean = this.formInstance.currentWorkflowStateIsStartState;
                this.buildFormInstance(allowDraftSave).then(built => {
                    if (built) {
                        this.emitOnSave();
                    }
                });
            });
        });
    }

    public emitOnSave() {
        let hshEventData = {
            formInstance: this.formInstance,
            form: this.form // Added to help solve circular dependency errors on save related to the grid cascading drop-down field.
        };

        this.formInstance.formButtonsDisabled = true;

        this.onSave.emit(hshEventData);

        this.bSavePending = true;

        if (this.formSettings.confirmSavePopup) {
            this.dialog.open(AlertDialogComponent, {
                data: new AlertDialogModel(
                    `Form Saved`,
                    `The form data was saved.`,
                    '', '', true)
            });
        }
    }

    public get SaveButtonCaption(): string {
        if (this.formSettings?.saveButtonCaption?.length > 0) return this.formSettings.saveButtonCaption;
        if (this.formInstance.currentWorkflowStateIsStartState) {
            return "Save Draft";
        } else {
            return "Save";
        }
    }

    public get AccessLostAfterWorkflowTransition(): boolean {
        return this.formInstance?.accessLostAfterTransition;
    }

    // saveCompleted() -- called by the form filler component.
    public savedCompleted(): void {
        this.formInstance.transientFormInstanceIsSaving = false;
        for (let component of this.formFieldComponentWrappers) {
            component.saveCompleted(this.formInstance);
        }

        // Trigger any conditional logic rules.
        /*
        setTimeout(() => {
            // Allow a framework life cycle to complete before triggering the logic.
            this.triggerAnyConditionalLogicRules();
        }, 0);
        */
        // Request field conditional logic instructions.
        //this.requestFieldConditionalLogicInstructions();
        //this.loadConditionalLogicComponentInfo();
        //this.requestFieldConditionalLogicInstructions();
        setTimeout(() => {
            // Allow a framework life cycle to complete before triggering the logic.
            this.loadConditionalLogicComponentDataStructures();
            this.requestFieldConditionalLogicInstructions();
        }, 0);
    }

    public undoSelectedWorkflowTransition(eventData: any): void {
        this.formInstance.selectedWorkflowTransition = null;

        return;
    }

    // Reset the form to its orignal values.
    public resetClicked(): void {
        // handling a reset in the client will be complex, so for now just reload from the server
        // TODO: we should reset from a cached formInstance, set on init of the component, and after successful saves
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(
                `Reload Form`,
                `This action will discard any edits you have made and load the most recent changes to this form. Are you sure you want to reload the form?`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                this.formInstanceService.reloadFormContents(this.formInstance.id).then(res => {
                    this.formInstance = res;

                    let hshEventData = { formInstance: this.formInstance };
                    this.onReset.emit(hshEventData);

                    //TEAMS-561: KLW - Logic for validation and enabling the save button
                    this.saveDisabled = false;

                    this.formInstanceReset(); // Need to be called to re-apply any conditional logic rules.
                });
            }
        });

        return;
    }

    public closeClicked(): void {
        this.onClose.emit(this.formInstance);
    }

    // just propagates the event
    public handleNewObjectCreated(event: any) {
        this.newObjectCreated.emit(event);
    }

    public handleFormFieldOnBlur(formFieldComponent: FormFieldBaseComponent): void {
        let sourceFieldId: number = formFieldComponent.FormField.id;
        // 01-22-2025 new logic:  only send requests to the server for fields that are the source of some conditional logic.
        let sourceField: FormField = this.formInstance.form.formFields != null ? this.formInstance.form.formFields.find(ff => ff.id == sourceFieldId) : null;
        if (this.formInstance != null) {
            if ((sourceField != null) && sourceField.fieldIsSourceOfConditionalLogic) {
                this.fieldConditionalLogicService.sourceFieldValueChanged(this.formInstance, sourceFieldId).then(response => {
                    if ((response != null) && (response.targetFieldInstructions != null)) {
                        this.applyFieldConditionalLogicInstructions(response.targetFieldInstructions);
                    }
                });
            }
            // PJH - 12/12/2024 - added for VNEXT-1401 to allow non flex fields to serve as source for flex fields
            if (this.formInstance.id > 0) {
                let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formFieldComponent.FormField.fieldDefinitionClassName);
                let fieldLogic: IFieldDefinitionLogic = fieldDefinition.customLogicHandler;
                if (fieldLogic.isNonFlexFieldButCanParticipateInFlexFieldBehavior) {
                    // Flex fields will already have called flexibleSelectionFieldService.selectionFieldValueChanged() but other fields that change need to call this
                    // this.selectedValuesByFieldId[formFieldComponent.FormField.id] = formFieldComponent.FormInstanceElement.getValueAsStringArray();
                    this.flexibleSelectionFieldService.selectionFieldValueChanged(formFieldComponent.FormField, this.formInstance, formFieldComponent.FormInstanceElement, []);
                }
            }

            this.fieldValueConcatenatorService.updateEditedField(formFieldComponent);
        }
    }

    public handleConditionalLogicInstructions(conditionalLogicInstructions: TargetFieldConditionalLogicInstructions[]): void {
        // VNEXT-1383, in particular the third use case.
        this.applyFieldConditionalLogicInstructions(conditionalLogicInstructions);
    }

    private applyFieldConditionalLogicInstructions(targetFieldInstructions: TargetFieldConditionalLogicInstructions[]): void {
        let hashOfCompoundComponentsEvalulated = {};

        for (let index: number = 0; index < targetFieldInstructions.length; index++) {
            let targetFieldInstruction: TargetFieldConditionalLogicInstructions = targetFieldInstructions[index];

            let targetFormField: FormField = this.form.formFields.find(ff => ff.id == targetFieldInstruction.targetFieldId);
            let componentData: FormRendererComponentData = targetFormField != null ? this.findFormFieldComponentDataFor(targetFormField.name) : null;
            // 11-25-2024 note:  will continue to develop the following, commented out code block in the future.
            /*
            if (componentData == null) {
                let formFieldOnInitData: FormRendererFormFieldOnInitInfo = this.arrFormFieldOnInitData.find(oid => oid.formField.id == targetFormField.id);
                if (formFieldOnInitData != null) {
                    // TO DO:  RECREATE A componentData instance.
                }
            }                
            */
            let fieldLayoutCellInfo: LayoutCellInfo = this.form.formLayout.findCellInfoContainingFieldId(targetFieldInstruction.targetFieldId);
            if (fieldLayoutCellInfo == null)
                fieldLayoutCellInfo = this.form.formLayout.findCellInfoContaining(targetFieldInstruction.targetFieldName);

            if (componentData != null) {
                // Are we dealing with a compound component, e.g. a forms grid or a data grid?
                if (componentData.wrapperComponent.isCompoundObjectComponent() && (targetFieldInstruction.applyChildFieldAttributes || targetFieldInstruction.applyFilter)) {
                    // If we have not yet evalulated a conditional logic expression for this component, call a method to reset conditional logic.
                    if (hashOfCompoundComponentsEvalulated[targetFormField.id] == null) {
                        componentData.wrapperComponent.resetConditionalLogicSettings();

                        hashOfCompoundComponentsEvalulated[targetFormField.id] = componentData;
                    }

                    // We are dealing with a forms grid or a data grid.
                    // Apply any child attributes?
                    if (targetFieldInstruction.applyChildFieldAttributes) {
                        // Apply child field attributes.  
                        componentData.wrapperComponent.applyChildFieldAttributes(targetFieldInstruction.childFieldName, targetFieldInstruction.showTargetField, !targetFieldInstruction.makeTargetFieldEditable, targetFieldInstruction.makeTargetFieldRequired);
                    }

                    // Any filtering?
                    if ((targetFieldInstruction.filterValue != null) && (targetFieldInstruction.filterValue.trim() != '') &&
                        (targetFieldInstruction.childFieldName != null) && (targetFieldInstruction.childFieldName.trim() != '')) {
                        // Yes:  tell the component to apply the filter.
                        componentData.wrapperComponent.applyFilter(targetFieldInstruction.filterValue, targetFieldInstruction.filterOperator, targetFieldInstruction.childFieldName);
                    }
                } else {
                    // We are dealing with an ordinary field.
                    if (fieldLayoutCellInfo != null)
                        fieldLayoutCellInfo.cell.transientIsHidden = !targetFieldInstruction.showTargetField;

                    let actionInstructions: FormFieldActionInstructions = this.conditionalLogicStructures.mapOfFormFieldInstructionsByAction[targetFieldInstruction.showTargetField ? 'Show' : 'Hide'];
                    let actionAppliedFlags: FormFieldActionAppliedFlags = new FormFieldActionAppliedFlags();
                    FormRendererComponent.applyStylesUsing(actionInstructions.applyActionStylesToRemove, componentData.elementRef, this.renderer, 'removeClass', actionAppliedFlags);
                    FormRendererComponent.applyStylesUsing(actionInstructions.applyActionStyles, componentData.elementRef, this.renderer, 'addClass', actionAppliedFlags);

                    componentData.formField.readOnly = !targetFieldInstruction.makeTargetFieldEditable;
                    componentData.formField.required = targetFieldInstruction.makeTargetFieldRequired;
                }

                let fieUserUpdatedDataValue: boolean = componentData.wrapperComponent.FormInstanceElement != null ? componentData.wrapperComponent.FormInstanceElement.UserUpdatedData : true;
                componentData.wrapperComponent.formFieldUpdated(); // 03-14-2024:  added this method to FormFieldBaseComponent and called it here.
                if (fieUserUpdatedDataValue == false) {
                    // Restore the false value for this flag; otherwise, the form will behave as if the user entereed data for this field's value.
                    componentData.wrapperComponent.FormInstanceElement.UserUpdatedData = false;
                }

            } else {
                // Save this instruction in the hope of apply it to the target later.
                this.conditionalLogicStructures.pendingTargetFieldInstructions.push(targetFieldInstruction);
            }
        } // for
    }

    private reapplyAnyPendingFieldConditionalLogicInstructions(): void {
        if (this.targetFieldInstructionsArePending()) {
            let localPendingTargetFieldInstructions: TargetFieldConditionalLogicInstructions[] = this.conditionalLogicStructures.pendingTargetFieldInstructions;
            this.conditionalLogicStructures.pendingTargetFieldInstructions = [];

            this.applyFieldConditionalLogicInstructions(localPendingTargetFieldInstructions);
        }
    }
    private targetFieldInstructionsArePending(): boolean {
        return this.conditionalLogicStructures.pendingTargetFieldInstructions.length > 0;
    }

    public formInstanceReset(): void {
        // Trigger any conditional logic rules.
        setTimeout(() => {
            // Allow a framework life cycle to complete before triggering the logic.
            this.triggerAnyConditionalLogicRules();
            this.requestFlexFieldInstructions();
        }, 0);
    }

    @Scriptable('Record Entered Field Values')
    public recordEnteredFieldValues(): void {
        this.recordedFieldValues = [];
    }

    @Scriptable('Validate Recorded Field Values')
    public validateRecordedValuesAndEndRecording() {
        // TO DO:  IMPLEMENT THIS METHOD.

        this.recordedFieldValues = null;
    }

    @Scriptable('Set Field Value')
    public setFieldValue(fieldName: string, fieldValue: string): void {
        if ((this.formFieldComponentWrappers != null) && (this.formFieldComponentWrappers.length > 0)) {
            let formFieldWrapperComponents: FormFieldWrapperComponent[] = this.formFieldComponentWrappers.toArray();

            for (let index: number = 0; index < formFieldWrapperComponents.length; index++) {
                let formFieldWrapperComponent: FormFieldWrapperComponent = formFieldWrapperComponents[index];
                let wrappedComponent: FormFieldBaseComponent = formFieldWrapperComponent.WrappedFormComponent;
                let formField: FormField = wrappedComponent.FormField;

                if ((formField != null) && ((formField.displayName == fieldName) || (formField.name == fieldName))) {
                    let formInstanceElement: FormInstanceElement = wrappedComponent.FormInstanceElement;
                    if (formInstanceElement != null) {
                        let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formField.fieldDefinitionClassName);
                        let fieldLogic: IFieldDefinitionLogic = fieldDefinition.customLogicHandler;
                        fieldLogic.pasteDataForInto(formField, formInstanceElement, fieldValue);

                        if (this.recordedFieldValues != null) {
                            let recordedFieldValue: RecordedSetFieldDataValue = new RecordedSetFieldDataValue(fieldName, fieldValue);
                            this.recordedFieldValues.push(recordedFieldValue);
                        }
                    }

                    break;
                }
            }
        }
    }

    // Implement private methods.
    private assignCellFormInstanceElement(): void {
        if (this.formInstance != null) {
            if ((this.formInstance.formInstanceElements !== undefined) &&
                (this.formInstance.formInstanceElements !== null)) {
                // Save form instance elements in a hash, keyed by form field ID.
                for (let iElement: number = 0; iElement < this.formInstance.formInstanceElements.length; iElement++) {
                    let formInstanceElement: FormInstanceElement = this.formInstance.formInstanceElements[iElement];

                    this.hshFormInstanceElements[formInstanceElement.formFieldId] = formInstanceElement;
                }

                // Assign form instance elements to any corresponding layout cells.
                let iNumRows: number = this.form.formLayout.rows.length;

                for (let iRow: number = 0; iRow < iNumRows; iRow++) {
                    let row: LayoutRow = this.form.formLayout.rows[iRow];

                    let iNumCells: number = row.cells.length;

                    for (let iCell: number = 0; iCell < iNumCells; iCell++) {
                        let cell: LayoutCell = row.cells[iCell];

                        let cellFormInstanceElement: FormInstanceElement = this.hshFormInstanceElements[cell.formFieldId];
                        if (cellFormInstanceElement == null)
                            cellFormInstanceElement = this.formInstance.formInstanceElements != null ? this.formInstance.formInstanceElements.find(fie => fie.formFieldName == cell.name) : null;
                        if (cellFormInstanceElement) {
                            // Associate a pre-existing form instance element with this cell.
                            cell.formInstanceElement = cellFormInstanceElement;
                        } else if (cell.formFieldId > 0) {
                            // Create and save a reference to the cell's
                            // initially blank form instance element.
                            cellFormInstanceElement = new FormInstanceElement();
                            cellFormInstanceElement.formInstanceId = this.formInstance.id;
                            cellFormInstanceElement.formFieldId = cell.formFieldId;

                            cell.formInstanceElement = cellFormInstanceElement;

                            this.hshFormInstanceElements[cell.formFieldId] = cell.formInstanceElement;
                        }
                    }
                } // end for
            } // end if
        } // end if

        return;
    }

    private setFieldsReadOnlyIfUserCannotEdit(): void {
        // Note:  the following line, when not commented out variable can be used for testing this functionality.
        //let forceReadOnly: boolean = true; 

        if ((this.formInstance != null) && (this.form != null)) {
            if (!this.formInstance.canEdit) { // || forceReadOnly) {
                if (this.form.formFields != null) {
                    for (let index: number = 0; index < this.form.formFields.length; index++) {
                        let formField: FormField = this.form.formFields[index];
                        formField.readOnly = true;
                    }
                }
            }
        }
    }

    //TEAMS-178: KLW - This method is needed to check if any changes have been made to the objects in the form
    public hasUnsavedChanges(): boolean {
        let hasUpdates: boolean = false;
        if (this.formInstance) {
            for (let el of this.formInstance.formInstanceElements) {
                if (el.UserUpdatedData) {
                    hasUpdates = true;
                    break;
                }
            }
        }
        return hasUpdates;
    }

    public roleSelectionChanged(): void {
        this.performShowToHideFromLogicIfApplicable();
    }

    // Helper methods.
    private performConditionalLogicRuleSetupIfApplicable(): void {
        // Do we have form field expressions?  If so, set up an expression 'listener'.
        this.expressionListener = ((this.form.conditionalLogicRules != null) && (this.form.conditionalLogicRules.length > 0) ? new FormFieldExpressionListener(this.form, <FormModeEnum>this.mode, this, this.fieldDefinitionService) : null);

        // If we have an expression listener, perform any conditional logic based on the form instance's initial field values.
        this.triggerAnyConditionalLogicRules();
    }

    private triggerAnyConditionalLogicRules(): void {
        // Trigger any conditional logic rules.
        // Note:  the following logic works in both form preview and form instance modes.
        if ((this.expressionListener != null) && (this.formFieldComponentWrappers != null) && (this.formFieldComponentWrappers.length > 0)) {
            let formFieldWrapperComponents: FormFieldWrapperComponent[] = this.formFieldComponentWrappers.toArray();

            for (let index: number = 0; index < formFieldWrapperComponents.length; index++) {
                let formFieldWrapperComponent: FormFieldWrapperComponent = formFieldWrapperComponents[index];
                let wrappedComponent: FormFieldBaseComponent = formFieldWrapperComponent.WrappedFormComponent;

                if ((wrappedComponent != null) && (wrappedComponent.FormInstanceElement != null) && (wrappedComponent.FormField != null))
                    this.expressionListener.formFieldValueChanged(wrappedComponent.FormField, wrappedComponent.FormInstanceElement);
            }
        }
    }

    private determineIfFormHasShowToHideFromLogic(): void {
        let hasShowToHideFromLogic: boolean = false;
        if ((this.mode == FormModeEnum.PREVIEW) && (this.form.id > 0) && (this.form.formFields != null)) {
            // Pass 1 of 2.
            for (let index: number = 0; index < this.form.formFields.length; index++) {
                let formField: FormField = this.form.formFields[index];
                formField.transientIsDeleted = false; // Reset this.

                let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formField.fieldDefinitionClassName);

                //if (formField.IsShowToOrHideFromField) {
                //if (fieldDefinition.isShowToHideFromConditional) {
                if (fieldDefinition.appliesRoleBasedBehavior) {
                    hasShowToHideFromLogic = true;
                    // Cannot break as we are also resetting 'transientIsDeleted' property values.
                }
            }
        }
        this.showToHideFromProperties.hasShowToHideFromLogic = hasShowToHideFromLogic;
    }

    private performShowToHideFromLogicIfApplicable(): void {
        // Note:  the following block has been moved into the method just above.
        /*
        let hasShowToHideFromLogic: boolean = false;
        if ((this.mode == FormModeEnum.PREVIEW) && (this.form.id > 0) && (this.form.formFields != null)) {
            // Pass 1 of 2.
            for (let index: number = 0; index < this.form.formFields.length; index++) {
                let formField: FormField = this.form.formFields[index];
                formField.transientIsDeleted = false; // Reset this.

                let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formField.fieldDefinitionClassName);

                //if (formField.IsShowToOrHideFromField) {
                //if (fieldDefinition.isShowToHideFromConditional) {
                if (fieldDefinition.appliesRoleBasedBehavior) {
                    hasShowToHideFromLogic = true;
                    // Cannot break as we are also resetting 'transientIsDeleted' property values.
                }
            }
        }
        this.showToHideFromProperties.hasShowToHideFromLogic = hasShowToHideFromLogic;
        */

        // Keep track of the removed rows and fields as
        // it gives us a way to check the server's
        // execution of the same logic.
        let debugListOfRemovedLayoutRows: LayoutRow[] = [];
        let debugListOfRemovedFormFields: FormField[] = [];

        // Do we have show to hide/hide from logic?
        if (this.showToHideFromProperties.hasShowToHideFromLogic) {
            // Pass 2 of 2.
            let preRenderFilters: IFormPreRenderFilter[] = [];
            let poppedFilter: IFormPreRenderFilter = null;

            for (let rowIndex: number = 0; rowIndex < this.form.formLayout.rows.length; rowIndex++) {
                let layoutRow: LayoutRow = this.form.formLayout.rows[rowIndex];
                layoutRow.transientIsDeleted = false; // Just resetting it here, and it can be marked as deleted below.
                let rowToKeep: LayoutRow = layoutRow;

                for (let cellIndex: number = 0; cellIndex < layoutRow.cells.length; cellIndex++) {
                    let layoutCell: LayoutCell = layoutRow.cells[cellIndex];
                    if (layoutCell.hasContent) {
                        let formField: FormField = this.form.formFields.find(ff => ff.name == layoutCell.name);
                        if (formField != null) {
                            let customLogicHandler: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(formField.fieldDefinitionClassName);
                            let preRenderFilter: IFormPreRenderFilter = customLogicHandler.pushFormPreRenderFilterFor(formField);

                            if (preRenderFilter != null) {
                                // Add this filter to the stack of actve filters.
                                preRenderFilters.push(preRenderFilter);
                                // Do not show rows occupied by "Show To" or "Hide From" fields.
                                layoutRow.transientIsDeleted = true;
                                rowToKeep = null;
                            } else if ((preRenderFilters.length > 0) &&
                                customLogicHandler.popFormPreRenderFilter(preRenderFilters[preRenderFilters.length - 1], formField)) {
                                poppedFilter = preRenderFilters.pop();
                                console.log('Popped a filter.');

                                // Do not show rows occupied by "Show To" or "Hide From" fields.
                                layoutRow.transientIsDeleted = true;
                                rowToKeep = null;
                            } else if (preRenderFilters.length > 0) {
                                // Do we need to filter out the current field?
                                let hideElement: boolean = false;
                                let markFieldReadOnly: boolean = false;

                                for (let filterIndex: number = 0; filterIndex < preRenderFilters.length; filterIndex++) {
                                    let preRenderFilter: IFormPreRenderFilter = preRenderFilters[filterIndex];
                                    if (!preRenderFilter.allowFormInstanceElement(this.form, formField, this.showToHideFromProperties.selectedRoleNames, null)) {
                                        hideElement = true;
                                        break;
                                    } else if (preRenderFilter.markFieldAsReadOnly(this.form, formField, this.showToHideFromProperties.selectedRoleNames, null)) {
                                        markFieldReadOnly = true;
                                        break;
                                    }
                                }

                                if (hideElement) {
                                    formField.transientIsDeleted = true;
                                    debugListOfRemovedFormFields.push(formField);

                                    layoutRow.transientIsDeleted = true;
                                    rowToKeep = null;
                                } else if (markFieldReadOnly) {
                                    formField.readOnly = true;
                                }
                            } // if-else if
                        } // if
                    } // if
                } // for

                if (rowToKeep == null)
                    debugListOfRemovedLayoutRows.push(layoutRow);
            } // for
        } // if
    }

    private loadConditionalLogicComponentDataStructures(): void {
        // Set the values for two parallel arrays that are used to apply field conditional logic.
        if (this.formFieldComponentWrappers && (this.formFieldComponentWrappers.length > 0)) {
            this.conditionalLogicStructures.formFieldComponentWrappersArray = this.formFieldComponentWrappers.toArray();
        }
        if (this.formFieldComponentElementRefs && (this.formFieldComponentElementRefs.length > 0)) {
            this.conditionalLogicStructures.formFieldComponentElementRefsArray = this.formFieldComponentElementRefs.toArray();
        }

        // Index form field component wrapper element refs too.
        /*
        if (this.formFieldComponentElementRefs && (this.formFieldComponentElementRefs.length > 0)) {
            let arrFormFieldComponentElementRefs: ElementRef[] = this.formFieldComponentElementRefs.toArray();

            if (arrFormFieldComponentElementRefs.length == this.conditionalLogicStructures.formFieldComponentWrappersArray.length) {
                this.conditionalLogicStructures.mapOfFormFieldComponentElementRefsById = {};

                for (let iElem: number = 0; iElem < arrFormFieldComponentElementRefs.length; iElem++) {
                    let elementRef: ElementRef = arrFormFieldComponentElementRefs[iElem];

                    let wrapperComponent: FormFieldWrapperComponent = this.conditionalLogicStructures.formFieldComponentWrappersArray[iElem];
                    let formField: FormField = wrapperComponent.FormField;
                    if (formField) {
                        let iFormFieldClientId: number = formField.clientId;

                        this.conditionalLogicStructures.mapOfFormFieldComponentElementRefsById[iFormFieldClientId] = elementRef;
                    }
                }
            }
        }
        */
    }

    private initiallyHideFieldsThatAreTargetsOfConditionalLogic(): void {
        // Mark layout cells for fields that are targets of conditional logic as hidden
        // pending the return of conditional logic instructions from the server.
        if ((this.form != null) && (this.form.formLayout != null) && (this.form.formLayout.rows != null)) {
            for (let rowIndex: number = 0; rowIndex < this.form.formLayout.rows.length; rowIndex++) {
                let layoutRow: LayoutRow = this.form.formLayout.rows[rowIndex];

                if (layoutRow.cells != null) {
                    for (let cellIndex: number = 0; cellIndex < layoutRow.cells.length; cellIndex++) {
                        let layoutCell: LayoutCell = layoutRow.cells[cellIndex];
                        let cellFormField: FormField = layoutCell.formFieldId > 0 ? this.form.formFields.find(ff => ff.id == layoutCell.formFieldId) : null;
                        if ((cellFormField != null) && cellFormField.fieldIsTargetOfConditionalLogic && (!cellFormField.fieldIsAlwaysShown)) {
                            // Mark this field as hidden, provisionally/temporarily, as it is a target of conditional logic.
                            // Perform this logic in the background to avoid the dreaded "ExpressionChangedAfterItHasBeenCheckedError" error.
                            //layoutCell.transientIsHidden = true;
                            setTimeout(() => {
                                layoutCell.transientIsHidden = true;
                            }, 0);

                        }
                    }
                }
            }
        }
    }

    private requestFieldConditionalLogicInstructions(): void {
        if (this.formInstance && this.formInstance.id > 0) {
            // We are displaying a form instance.
            // Request conditional logic guidance if the user has the ability to interact with the form, i.e. "canEdit == true".
            // 06-24-2024:  changing to requesting conditional logic instructions if the user can view the form.
            if (this.formInstance.canView && (!this.formInstance.formButtonsDisabled)) {
                this.fieldConditionalLogicService.getFormInstanceLoadedTargetFieldInstructions(this.formInstance.id).then(response => {
                    this.applyFieldConditionalLogicInstructions(response.targetFieldInstructions);
                });
            }
        } else if (this.currentSiteService.Site.siteHomePage) {
            // We are displaying a site home page.
            this.fieldConditionalLogicService.getSiteHomePageLoadedTargetFieldInstructions(this.currentSiteService.Site.id).then(response => {
                this.applyFieldConditionalLogicInstructions(response.targetFieldInstructions);
            });
        }
    }

    private requestFlexFieldInstructions(): void {
        if (this.formInstance && this.formInstance.id > 0) {
            this.flexibleSelectionFieldService.requestFlexibleFieldInstructionsForFormInstance(this.formInstance.id);
        } else {
            // PJH - VNEXT-1641 - flex fields on Site Home Page - if there is no formInstance, we must be on the Site Home Page
            this.flexibleSelectionFieldService.requestFlexibleFieldInstructionsForSiteHomePage(this.formInstance.form.id);
        }
    }

    private populateSelectedValuesByFieldIdForFlexFields(): void {
        if (this.formInstance?.id > 0) {
            this.selectedValuesByFieldId = this.formInstance.getSelectedValuesByFieldId();
        }
    }
}
